// src/pages/PanelFirma/CartasPoder.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Tooltip, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    LinearProgress, Checkbox, TableSortLabel, TablePagination
} from '@mui/material';
import {
    Visibility as ViewIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    GetApp as DownloadIcon,
    Description as DescriptionIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    VpnKey as TokenIcon,
    CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import jsPDF from 'jspdf';
import { ESCUDO_BASE64, ESCUDO_WIDTH_MM, ESCUDO_HEIGHT_MM } from '../../utils/escudoFiscalia';
import { fetchFirmaLocal } from '../../hooks/useServicioFirma';

import {
    panelFirmaPendientesJWT,
    panelFirmaDocumentoJWT,
    panelFirmaRechazarJWT,
    panelFirmaSubirPdfJWT,
    panelFirmaSubirPdfFirmadoJWT,
    panelFirmaDescargarPdfJWT,
} from '../../api/endpoints';
import CartaPoderDetalle from './CartaPoderDetalle';

const styles = {
    paper: {
        padding: '16px',
        marginBottom: '16px',
        borderRadius: '4px',
    },
    filterRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    chipFiltro: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
    },
    chipActivo: {
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.5)',
    },
    tableContainer: {
        maxHeight: 550,
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
    },
    bulkBar: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        marginBottom: '8px',
        padding: '8px 16px',
        backgroundColor: '#42a5f515',
        borderRadius: '4px',
        border: '1px solid #42a5f5',
        flexWrap: 'wrap',
    },
};

const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

const CartasPoder = ({ acceso, onRefreshStats }) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState('PENDIENTE'); // default pendientes
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [detalleDoc, setDetalleDoc] = useState(null); // { doc, contenidoHtml }
    const [rechazarDialog, setRechazarDialog] = useState({ open: false, id: null, motivo: '' });
    const [selected, setSelected] = useState([]);
    const [orderBy, setOrderBy] = useState('Fecha_Solicitud');
    const [orderDir, setOrderDir] = useState('desc');
    const [generandoPdf, setGenerandoPdf] = useState(null);
    const [servicioFirma, setServicioFirma] = useState({ disponible: null, tokens: 0 });
    const [firmaDialog, setFirmaDialog] = useState({ open: false, pin: '', firmando: false });
    const [progresoDialog, setProgresoDialog] = useState({ open: false, total: 0, actual: 0, generados: 0, errores: 0, currentName: '', tipo: '' });
    const [uploadDialog, setUploadDialog] = useState({ open: false, docId: null });

    const cargarItems = useCallback(async () => {
        try {
            setLoading(true);
            const data = await panelFirmaPendientesJWT('CARTA_PODER', null);
            if (data?.ok) setItems(data.items || []);
        } catch (err) {
            enqueueSnackbar('Error al cargar documentos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        cargarItems();
        verificarServicioFirma();
    }, [cargarItems]);

    // Polling: verificar servicio de firma cada 5 segundos
    useEffect(() => {
        const interval = setInterval(verificarServicioFirma, 5000);
        return () => clearInterval(interval);
    }, []);

    const verificarServicioFirma = async () => {
        try {
            const resp = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
            if (resp.ok) {
                const data = await resp.json().catch(() => ({}));
                setServicioFirma({ disponible: true, tokens: data.tokensDetectados ?? 0 });
            } else {
                setServicioFirma({ disponible: false, tokens: 0 });
            }
        } catch (err) {
            if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
                try {
                    await fetch(`${FIRMA_SERVICE_URL}/status`, { mode: 'no-cors', signal: AbortSignal.timeout(2000) });
                    setServicioFirma({ disponible: true, tokens: 0 });
                } catch {
                    setServicioFirma({ disponible: false, tokens: 0 });
                }
            } else {
                setServicioFirma({ disponible: false, tokens: 0 });
            }
        }
    };

    // Filtering
    const conteos = {
        total: items.length,
        pendientes: items.filter(i => i.Estado === 'PENDIENTE').length,
        firmados: items.filter(i => i.Estado === 'FIRMADO').length,
        rechazados: items.filter(i => i.Estado === 'RECHAZADO').length,
    };

    const itemsFiltrados = items.filter(i => {
        if (filtroEstado && i.Estado !== filtroEstado) return false;
        if (filtroBusqueda) {
            const txt = filtroBusqueda.toLowerCase();
            return (
                (i.Numero || '').toLowerCase().includes(txt) ||
                (i.Caratula || '').toLowerCase().includes(txt) ||
                (i.Autos || '').toLowerCase().includes(txt) ||
                (i.Solicitante || '').toLowerCase().includes(txt)
            );
        }
        return true;
    });

    // Sorting
    const handleSort = (col) => {
        if (orderBy === col) setOrderDir(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(col); setOrderDir('asc'); }
    };

    const sorted = [...itemsFiltrados].sort((a, b) => {
        const vA = a[orderBy] ?? '';
        const vB = b[orderBy] ?? '';
        let cmp = typeof vA === 'number' && typeof vB === 'number'
            ? vA - vB
            : String(vA).localeCompare(String(vB), 'es', { sensitivity: 'base' });
        return orderDir === 'asc' ? cmp : -cmp;
    });

    // Selection
    const handleSelectAll = (e) => {
        setSelected(e.target.checked ? sorted.map(i => i.Id) : []);
    };
    const handleSelectOne = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // View document detail
    const handleVerDocumento = async (item) => {
        try {
            const data = await panelFirmaDocumentoJWT(item.Id);
            if (data?.ok && data.documento) {
                setDetalleDoc({
                    doc: data.documento,
                    idExpediente: item.Id_Expediente || data.documento.Id_Expediente,
                });
            }
        } catch (err) {
            enqueueSnackbar('Error al obtener documento', { variant: 'error' });
        }
    };


    // Reject
    const handleRechazar = async () => {
        const { id, motivo } = rechazarDialog;
        if (!motivo.trim()) {
            enqueueSnackbar('Ingrese un motivo', { variant: 'warning' });
            return;
        }
        try {
            const data = await panelFirmaRechazarJWT(id, motivo);
            if (data?.ok) {
                enqueueSnackbar('Documento rechazado', { variant: 'success' });
                setRechazarDialog({ open: false, id: null, motivo: '' });
                await cargarItems();
                onRefreshStats?.();
            }
        } catch (err) {
            enqueueSnackbar('Error al rechazar', { variant: 'error' });
        }
    };

    // PDF generation (reuse logic from FirmaCartasPoder)
    const generarPdfDeCarta = async (docId) => {
        const data = await panelFirmaDocumentoJWT(docId);
        if (!data?.ok || !data.documento?.EscritoContenido) return null;
        const html = data.documento.EscritoContenido;

        const domParser = new DOMParser();
        const htmlDoc = domParser.parseFromString(html, 'text/html');

        let margins = { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 };
        const marginEl = htmlDoc.querySelector('[data-sigif-margins]');
        if (marginEl) {
            const marginStr = marginEl.getAttribute('data-sigif-margins');
            marginStr.split(';').forEach(part => {
                const [key, val] = part.split(':');
                if (key && val) margins[key.trim()] = parseFloat(val);
            });
        }

        const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const contentW = pageW - margins.left - margins.right;
        let cursorY = margins.top;

        const FONT = 'times';
        const FONT_SIZE = 12;
        const PT_TO_MM = 0.3528;

        const checkPageBreak = (needed) => {
            if (cursorY + needed > pageH - margins.bottom) { doc.addPage(); cursorY = margins.top; }
        };

        const extractTextRuns = (node) => {
            const runs = [];
            const walk = (el, inherited) => {
                if (el.nodeType === Node.TEXT_NODE) {
                    const text = el.textContent;
                    if (text.trim() || text.includes(' ')) runs.push({ text, ...inherited });
                    return;
                }
                if (el.nodeType !== Node.ELEMENT_NODE) return;
                if (el.tagName.toLowerCase() === 'img') return;
                const tag = el.tagName.toLowerCase();
                const style = {};
                if (tag === 'b' || tag === 'strong' || (el.style && parseInt(el.style.fontWeight) >= 700)) style.bold = true;
                if (tag === 'u' || (el.style && el.style.textDecoration === 'underline')) style.underline = true;
                for (const child of el.childNodes) walk(child, { ...inherited, ...style });
            };
            walk(node, {});
            return runs;
        };

        const renderFormattedText = (runs, align, lineHeightMm) => {
            if (runs.length === 0) return;
            doc.setFontSize(FONT_SIZE);
            const lh = lineHeightMm;
            const words = [];
            for (const run of runs) {
                const parts = run.text.replace(/\s+/g, ' ').split(' ');
                for (const p of parts) {
                    if (p.length > 0) words.push({ text: p, bold: !!run.bold, underline: !!run.underline });
                }
            }
            if (words.length === 0) return;
            doc.setFont(FONT, 'normal');
            const spaceW = doc.getTextWidth(' ');
            const lines = [];
            let currentLine = [], currentWidth = 0;
            for (const word of words) {
                doc.setFont(FONT, word.bold ? 'bold' : 'normal');
                const wordW = doc.getTextWidth(word.text);
                const neededW = currentLine.length > 0 ? spaceW + wordW : wordW;
                if (currentWidth + neededW > contentW && currentLine.length > 0) {
                    lines.push([...currentLine]);
                    currentLine = [word]; currentWidth = wordW;
                } else {
                    currentLine.push(word); currentWidth += neededW;
                }
            }
            if (currentLine.length > 0) lines.push(currentLine);

            for (let li = 0; li < lines.length; li++) {
                const line = lines[li];
                checkPageBreak(lh);
                let wordsWidth = 0;
                for (const w of line) {
                    doc.setFont(FONT, w.bold ? 'bold' : 'normal');
                    wordsWidth += doc.getTextWidth(w.text);
                }
                const totalWithNormalSpaces = wordsWidth + spaceW * (line.length - 1);
                const isLastLine = li === lines.length - 1;
                const shouldJustify = align === 'justify' && !isLastLine && line.length > 1;
                let gapBetweenWords = shouldJustify ? (contentW - wordsWidth) / (line.length - 1) : spaceW;
                let x = margins.left;
                if (align === 'center' && !shouldJustify) x = margins.left + (contentW - totalWithNormalSpaces) / 2;
                else if (align === 'right' && !shouldJustify) x = margins.left + contentW - totalWithNormalSpaces;
                for (let wi = 0; wi < line.length; wi++) {
                    const w = line[wi];
                    doc.setFont(FONT, w.bold ? 'bold' : 'normal');
                    doc.text(w.text, x, cursorY);
                    const tw = doc.getTextWidth(w.text);
                    if (w.underline) { doc.setLineWidth(0.3); doc.line(x, cursorY + 0.5, x + tw, cursorY + 0.5); }
                    if (wi < line.length - 1) x += tw + gapBetweenWords;
                }
                cursorY += lh;
            }
        };

        const getAlign = (el) => {
            const s = el.getAttribute('style') || '';
            if (s.includes('text-align: center') || s.includes('text-align:center')) return 'center';
            if (s.includes('text-align: right') || s.includes('text-align:right')) return 'right';
            if (s.includes('text-align: justify') || s.includes('text-align:justify')) return 'justify';
            return 'left';
        };
        const getLineHeight = (el) => {
            const s = el.getAttribute('style') || '';
            const m = s.match(/line-height:\s*([\d.]+)(%|;|\s|$)/);
            if (m) { let v = parseFloat(m[1]); if (m[2] === '%') v /= 100; if (v < 10) return FONT_SIZE * PT_TO_MM * v; }
            return FONT_SIZE * PT_TO_MM * 1.15;
        };
        const getMarginBottom = (el) => {
            const s = el.getAttribute('style') || '';
            if (s.includes('margin-bottom: 0') || s.includes('margin-bottom:0')) return 0;
            return 8 * PT_TO_MM;
        };

        // Si el HTML no contiene ninguna imagen (escudo), agregar escudo al inicio
        const tieneImagen = htmlDoc.body.querySelector('img') !== null;
        if (!tieneImagen && ESCUDO_BASE64) {
            try {
                checkPageBreak(ESCUDO_HEIGHT_MM + 2);
                const imgX = margins.left + (contentW - ESCUDO_WIDTH_MM) / 2;
                doc.addImage(ESCUDO_BASE64, 'PNG', imgX, cursorY, ESCUDO_WIDTH_MM, ESCUDO_HEIGHT_MM);
                cursorY += ESCUDO_HEIGHT_MM + 3;
            } catch (e) { /* skip */ }
        }

        const elements = htmlDoc.body.querySelectorAll(':scope > p, :scope > div');
        for (const el of elements) {
            if (el.id === 'sigif-margins' || (el.style && el.style.display === 'none')) continue;
            const align = getAlign(el);
            const lineH = getLineHeight(el);
            const marginBottom = getMarginBottom(el);
            const img = el.querySelector('img');
            if (img && img.src) {
                try {
                    const imgW = parseInt(img.getAttribute('width') || '76');
                    const imgH = parseInt(img.getAttribute('height') || '120');
                    const pxToMm = 25.4 / 96;
                    const wMm = imgW * pxToMm; const hMm = imgH * pxToMm;
                    checkPageBreak(hMm + 2);
                    const imgX = align === 'center' ? margins.left + (contentW - wMm) / 2 : margins.left;
                    doc.addImage(img.src, 'PNG', imgX, cursorY, wMm, hMm);
                    cursorY += hMm + 3;
                } catch { /* skip */ }
            } else {
                const text = el.textContent.replace(/\s+/g, ' ').trim();
                if (!text || text === '\u00a0') { cursorY += lineH * 0.3; continue; }
                renderFormattedText(extractTextRuns(el), align, lineH);
            }
            cursorY += marginBottom;
        }

        return doc.output('blob');
    };

    const handleGenerarPdf = async (docId) => {
        setGenerandoPdf(docId);
        try {
            const pdfBlob = await generarPdfDeCarta(docId);
            if (!pdfBlob) { enqueueSnackbar('No se pudo generar PDF', { variant: 'error' }); return; }
            const resp = await panelFirmaSubirPdfJWT(docId, pdfBlob);
            if (resp?.ok) {
                enqueueSnackbar('PDF generado', { variant: 'success' });
                await cargarItems();
            }
        } catch (err) {
            enqueueSnackbar('Error al generar PDF', { variant: 'error' });
        } finally {
            setGenerandoPdf(null);
        }
    };

    const handleDescargarPdf = async (docId, numero, firmado = false) => {
        try {
            const blob = await panelFirmaDescargarPdfJWT(docId, firmado);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `CartaPoder_${(numero || 'doc').replace(/\//g, '-')}${firmado ? '_firmado' : ''}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            enqueueSnackbar('Error al descargar PDF', { variant: 'error' });
        }
    };

    // Digital signature flow
    const handleAbrirFirmaDigital = () => {
        if (!servicioFirma.disponible) {
            enqueueSnackbar('Servicio de firma local no disponible. Verifique SIGIF Firma Local.', { variant: 'error' });
            return;
        }
        if (servicioFirma.tokens === 0) {
            enqueueSnackbar('Token no detectado. Inserte el token USB.', { variant: 'warning' });
            return;
        }
        setFirmaDialog({ open: true, pin: '', firmando: false });
    };

    const handleFirmarDigitalmente = async () => {
        const pin = firmaDialog.pin;
        if (!pin) { enqueueSnackbar('Ingrese el PIN del token', { variant: 'warning' }); return; }

        let cartasParaFirmar = sorted.filter(i => i.Estado === 'PENDIENTE' && i.Estado_Firma >= 1);
        if (selected.length > 0) cartasParaFirmar = cartasParaFirmar.filter(i => selected.includes(i.Id));
        if (cartasParaFirmar.length === 0) {
            enqueueSnackbar('No hay documentos con PDF pendientes de firma', { variant: 'warning' });
            return;
        }

        setFirmaDialog(prev => ({ ...prev, firmando: true }));
        setProgresoDialog({ open: true, total: cartasParaFirmar.length, actual: 0, generados: 0, errores: 0, currentName: '', tipo: 'firmar' });

        let firmados = 0, errores = 0;
        for (let i = 0; i < cartasParaFirmar.length; i++) {
            const item = cartasParaFirmar[i];
            setProgresoDialog(prev => ({ ...prev, actual: i + 1, currentName: item.Numero || `Doc ${i + 1}` }));
            try {
                // 1. Descargar PDF del backend
                const pdfBlob = await panelFirmaDescargarPdfJWT(item.Id, false);
                console.log(`[Firma] PDF descargado: ${pdfBlob.size} bytes, tipo: ${pdfBlob.type}`);

                // 2. Enviar al servicio local para firmar
                const firmaResp = await fetchFirmaLocal(`/firmar?pin=${encodeURIComponent(pin)}`, {
                    method: 'POST', body: pdfBlob, headers: { 'Content-Type': 'application/pdf' }
                });
                if (!firmaResp.ok) {
                    const errData = await firmaResp.json().catch(() => null);
                    console.error('[Firma] Error servicio local:', firmaResp.status, errData);
                    if (errData?.tipo === 'token') { enqueueSnackbar(errData.error || 'Error con token', { variant: 'error' }); break; }
                    throw new Error(errData?.error || `Error al firmar (${firmaResp.status})`);
                }
                const signedBlob = await firmaResp.blob();
                console.log(`[Firma] PDF firmado: ${signedBlob.size} bytes`);

                // 3. Subir PDF firmado al backend
                const upResp = await panelFirmaSubirPdfFirmadoJWT(item.Id, signedBlob);
                console.log('[Firma] Respuesta upload:', upResp);
                if (upResp?.ok) firmados++;
                else {
                    console.error('[Firma] Upload falló:', upResp);
                    enqueueSnackbar(upResp?.mensaje || 'Error al subir PDF firmado', { variant: 'error' });
                    errores++;
                }
            } catch (err) {
                console.error('[Firma] Error:', err);
                enqueueSnackbar(`Error: ${err.message}`, { variant: 'error' });
                errores++;
                // PIN mal o el cliente de firma se cerró: seguir solo sumaría errores
                if (err.cortar || err.message?.includes('PIN')) break;
            }
            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({ ...prev, generados: firmados, errores }));
        }

        await cargarItems();
        onRefreshStats?.();
        setProgresoDialog(prev => ({ ...prev, open: false }));
        setFirmaDialog({ open: false, pin: '', firmando: false });
        enqueueSnackbar(`${firmados} documento(s) firmado(s)${errores > 0 ? `, ${errores} error(es)` : ''}`, { variant: errores > 0 ? 'warning' : 'success' });
    };

    // Upload signed PDF manually
    const handleSubirFirmado = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const resp = await panelFirmaSubirPdfFirmadoJWT(uploadDialog.docId, file);
            if (resp?.ok) {
                enqueueSnackbar('PDF firmado subido correctamente', { variant: 'success' });
                setUploadDialog({ open: false, docId: null });
                await cargarItems();
                onRefreshStats?.();
            }
        } catch {
            enqueueSnackbar('Error al subir PDF firmado', { variant: 'error' });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Bulk generate PDFs
    const handleGenerarMasivo = async () => {
        const sinPdf = sorted.filter(i => selected.includes(i.Id) && i.Estado === 'PENDIENTE' && i.Estado_Firma === 0);
        if (sinPdf.length === 0) { enqueueSnackbar('No hay documentos sin PDF seleccionados', { variant: 'warning' }); return; }

        setProgresoDialog({ open: true, total: sinPdf.length, actual: 0, generados: 0, errores: 0, currentName: '', tipo: 'generar' });
        let generados = 0, errores = 0;
        for (let i = 0; i < sinPdf.length; i++) {
            const item = sinPdf[i];
            setProgresoDialog(prev => ({ ...prev, actual: i + 1, currentName: item.Numero || `Doc ${i + 1}` }));
            try {
                const pdfBlob = await generarPdfDeCarta(item.Id);
                if (pdfBlob) { await panelFirmaSubirPdfJWT(item.Id, pdfBlob); generados++; }
                else errores++;
            } catch { errores++; }
            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({ ...prev, generados, errores }));
        }
        await cargarItems();
        setProgresoDialog(prev => ({ ...prev, open: false }));
        enqueueSnackbar(`${generados} PDF(s) generado(s)${errores > 0 ? `, ${errores} error(es)` : ''}`, { variant: errores > 0 ? 'warning' : 'success' });
    };

    // Estado chip
    const getEstadoChip = (item) => {
        if (item.Estado === 'FIRMADO') return <Chip label="Firmado" size="small" color="success" />;
        if (item.Estado === 'RECHAZADO') return <Chip label="Rechazado" size="small" color="error" />;
        if (item.Estado_Firma === 0) return <Chip label="Sin PDF" size="small" />;
        if (item.Estado_Firma === 1) return <Chip label="Pendiente Firma" size="small" color="warning" />;
        return <Chip label="Pendiente" size="small" color="warning" />;
    };

    const formatFecha = (f) => {
        if (!f) return '-';
        try { return new Date(f).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }); }
        catch { return f; }
    };

    const selectedPendienteSinPdf = sorted.filter(i => selected.includes(i.Id) && i.Estado === 'PENDIENTE' && i.Estado_Firma === 0).length;
    const selectedPendienteConPdf = sorted.filter(i => selected.includes(i.Id) && i.Estado === 'PENDIENTE' && i.Estado_Firma >= 1).length;

    // If viewing a document detail
    if (detalleDoc) {
        return (
            <CartaPoderDetalle
                doc={detalleDoc.doc}
                idExpediente={detalleDoc.idExpediente}
                onVolver={() => setDetalleDoc(null)}
                onRechazar={() => {
                    setRechazarDialog({ open: true, id: detalleDoc.doc.Id, motivo: '' });
                    setDetalleDoc(null);
                }}
                onGenerarPdf={() => handleGenerarPdf(detalleDoc.doc.Id)}
                onDescargarPdf={(firmado) => handleDescargarPdf(detalleDoc.doc.Id, detalleDoc.doc.Numero, firmado)}
                generandoPdf={generandoPdf === detalleDoc.doc.Id}
                onRefresh={async () => { await cargarItems(); onRefreshStats?.(); }}
            />
        );
    }

    return (
        <>
            {/* Filters */}
            <Paper sx={styles.paper}>
                <Box sx={styles.filterRow}>
                    <Chip label={`${conteos.pendientes} Pendientes`} color={filtroEstado === 'PENDIENTE' ? 'warning' : 'default'}
                        onClick={() => { setFiltroEstado(filtroEstado === 'PENDIENTE' ? null : 'PENDIENTE'); setSelected([]); }}
                        sx={{...styles.chipFiltro, ...(filtroEstado === 'PENDIENTE' ? styles.chipActivo : {})}} />
                    <Chip label={`${conteos.firmados} Firmados`} color={filtroEstado === 'FIRMADO' ? 'success' : 'default'}
                        onClick={() => { setFiltroEstado(filtroEstado === 'FIRMADO' ? null : 'FIRMADO'); setSelected([]); }}
                        sx={{...styles.chipFiltro, ...(filtroEstado === 'FIRMADO' ? styles.chipActivo : {})}} />
                    <Chip label={`${conteos.rechazados} Rechazados`} color={filtroEstado === 'RECHAZADO' ? 'error' : 'default'}
                        onClick={() => { setFiltroEstado(filtroEstado === 'RECHAZADO' ? null : 'RECHAZADO'); setSelected([]); }}
                        sx={{...styles.chipFiltro, ...(filtroEstado === 'RECHAZADO' ? styles.chipActivo : {})}} />
                    <Chip label={`${conteos.total} Total`} color={!filtroEstado ? 'primary' : 'default'}
                        onClick={() => { setFiltroEstado(null); setSelected([]); }}
                        sx={{...styles.chipFiltro, ...(!filtroEstado ? styles.chipActivo : {})}} />

                    <Box sx={{ flexGrow: 1 }} />

                    <TextField
                        size="small" placeholder="Buscar..."
                        value={filtroBusqueda} onChange={(e) => setFiltroBusqueda(e.target.value)}
                        style={{ width: 300 }}
                        InputProps={{ startAdornment: <SearchIcon style={{ color: '#999', marginRight: 8 }} /> }}
                    />
                    <Tooltip title="Recargar">
                        <span><IconButton onClick={cargarItems} disabled={loading}><RefreshIcon /></IconButton></span>
                    </Tooltip>
                </Box>

                {/* Bulk actions */}
                {selected.length > 0 && (
                    <Box sx={styles.bulkBar}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{selected.length} seleccionada(s)</Typography>
                        {selectedPendienteSinPdf > 0 && (
                            <Button variant="contained" size="small" startIcon={<DescriptionIcon />} onClick={handleGenerarMasivo}>
                                Generar {selectedPendienteSinPdf} PDF(s)
                            </Button>
                        )}
                        {selectedPendienteConPdf > 0 && servicioFirma.disponible && servicioFirma.tokens > 0 && (
                            <Button variant="contained" size="small" style={{ backgroundColor: '#2e7d32', color: 'white' }}
                                startIcon={<TokenIcon />} onClick={handleAbrirFirmaDigital}>
                                Firmar {selectedPendienteConPdf} con Token
                            </Button>
                        )}
                        {selectedPendienteConPdf > 0 && (servicioFirma.disponible === false || servicioFirma.tokens === 0) && (
                            <Tooltip title="Servicio de firma local no disponible">
                                <span>
                                    <Button variant="outlined" size="small" style={{ borderColor: '#ccc', color: '#999' }}
                                        startIcon={<TokenIcon />} disabled>
                                        Firma no disponible
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button size="small" onClick={() => setSelected([])}>Deseleccionar</Button>
                    </Box>
                )}

                {/* Action bar */}
                {selected.length === 0 && (
                    <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
                        {servicioFirma.disponible && servicioFirma.tokens > 0 && (
                            <Button variant="contained" size="small" style={{ backgroundColor: '#2e7d32', color: 'white' }}
                                startIcon={<TokenIcon />} onClick={handleAbrirFirmaDigital}>
                                Firmar con Token USB
                            </Button>
                        )}
                        {(servicioFirma.disponible === false || servicioFirma.tokens === 0) && (
                            <Tooltip title={servicioFirma.disponible ? "Token no detectado. Inserte el token USB." : "El servicio de firma local no está disponible. Verifique que SIGIF Firma Local esté ejecutándose."}>
                                <span>
                                    <Button variant="outlined" size="small" style={{ borderColor: '#ccc', color: '#999' }}
                                        startIcon={<TokenIcon />} disabled>
                                        Firma Digital no disponible
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {loading ? (
                    <Box sx={styles.loadingBox}><CircularProgress /></Box>
                ) : (
                    <>
                    <TableContainer sx={styles.tableContainer}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selected.length > 0 && selected.length < sorted.length}
                                            checked={sorted.length > 0 && selected.length === sorted.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        <TableSortLabel active={orderBy === 'Numero'} direction={orderBy === 'Numero' ? orderDir : 'asc'} onClick={() => handleSort('Numero')}>
                                            Nro. Carta Poder
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        <TableSortLabel active={orderBy === 'Caratula'} direction={orderBy === 'Caratula' ? orderDir : 'asc'} onClick={() => handleSort('Caratula')}>
                                            Expediente
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>Solicitante</TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        <TableSortLabel active={orderBy === 'Fecha_Solicitud'} direction={orderBy === 'Fecha_Solicitud' ? orderDir : 'asc'} onClick={() => handleSort('Fecha_Solicitud')}>
                                            Fecha Solicitud
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>Estado</TableCell>
                                    <TableCell style={{ fontWeight: 600, textAlign: 'center' }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="text.secondary">No hay documentos</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, idx) => (
                                    <TableRow key={item.Id} hover selected={selected.includes(item.Id)}
                                        style={item.Estado === 'FIRMADO' ? { backgroundColor: '#e8f5e9' } : item.Estado === 'RECHAZADO' ? { backgroundColor: '#fce4ec' } : undefined}
                                        sx={{ backgroundColor: item.Estado === 'PENDIENTE' ? (idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent') : undefined }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selected.includes(item.Id)} onChange={() => handleSelectOne(item.Id)} />
                                        </TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{item.Numero || '-'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap style={{ maxWidth: 250 }}>
                                                {item.Caratula || '-'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">{item.Autos || ''}</Typography>
                                        </TableCell>
                                        <TableCell>{item.Solicitante || '-'}</TableCell>
                                        <TableCell>{formatFecha(item.Fecha_Solicitud)}</TableCell>
                                        <TableCell>{getEstadoChip(item)}</TableCell>
                                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Ver detalle">
                                                <IconButton size="small" onClick={() => handleVerDocumento(item)}><ViewIcon /></IconButton>
                                            </Tooltip>
                                            {item.Estado === 'PENDIENTE' && item.Estado_Firma === 0 && (
                                                <Tooltip title="Generar PDF">
                                                    <span>
                                                        <IconButton size="small" color="primary" onClick={() => handleGenerarPdf(item.Id)} disabled={generandoPdf === item.Id}>
                                                            {generandoPdf === item.Id ? <CircularProgress size={18} /> : <DescriptionIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {item.Estado === 'PENDIENTE' && item.Estado_Firma >= 1 && (
                                                <>
                                                    <Tooltip title="Descargar PDF">
                                                        <IconButton size="small" onClick={() => handleDescargarPdf(item.Id, item.Numero, false)}><DownloadIcon /></IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {item.Estado === 'FIRMADO' && (
                                                <Tooltip title="Descargar firmado">
                                                    <IconButton size="small" style={{ color: '#4caf50' }} onClick={() => handleDescargarPdf(item.Id, item.Numero, true)}>
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {item.Estado === 'PENDIENTE' && (
                                                <Tooltip title="Rechazar">
                                                    <IconButton size="small" color="error" onClick={() => setRechazarDialog({ open: true, id: item.Id, motivo: '' })}>
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={sorted.length}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[25, 50, 100]}
                        labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                    </>
                )}
            </Paper>

            {/* Rechazar dialog */}
            <Dialog open={rechazarDialog.open} onClose={() => setRechazarDialog({ open: false, id: null, motivo: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>Rechazar Documento</DialogTitle>
                <DialogContent>
                    <Typography paragraph variant="body2">Ingrese el motivo del rechazo. Se notificará al solicitante.</Typography>
                    <TextField
                        label="Motivo" fullWidth multiline rows={3}
                        value={rechazarDialog.motivo} onChange={(e) => setRechazarDialog(prev => ({ ...prev, motivo: e.target.value }))}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRechazarDialog({ open: false, id: null, motivo: '' })}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleRechazar} disabled={!rechazarDialog.motivo.trim()}>Rechazar</Button>
                </DialogActions>
            </Dialog>

            {/* Firma digital dialog */}
            <Dialog open={firmaDialog.open} onClose={() => !firmaDialog.firmando && setFirmaDialog({ open: false, pin: '', firmando: false })} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TokenIcon style={{ color: '#2e7d32' }} />
                        Firma Digital con Token USB
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Se firmarán <strong>
                            {selected.length > 0
                                ? sorted.filter(i => selected.includes(i.Id) && i.Estado === 'PENDIENTE' && i.Estado_Firma >= 1).length
                                : sorted.filter(i => i.Estado === 'PENDIENTE' && i.Estado_Firma >= 1).length}
                        </strong> documento(s) pendientes.
                    </Typography>
                    <TextField label="PIN del Token" type="password" fullWidth autoFocus
                        value={firmaDialog.pin} onChange={(e) => setFirmaDialog(prev => ({ ...prev, pin: e.target.value }))}
                        disabled={firmaDialog.firmando}
                        onKeyDown={(e) => e.key === 'Enter' && !firmaDialog.firmando && handleFirmarDigitalmente()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFirmaDialog({ open: false, pin: '', firmando: false })} disabled={firmaDialog.firmando}>Cancelar</Button>
                    <Button variant="contained" style={{ backgroundColor: '#2e7d32', color: 'white' }}
                        onClick={handleFirmarDigitalmente} disabled={firmaDialog.firmando || !firmaDialog.pin}
                        startIcon={firmaDialog.firmando ? <CircularProgress size={18} color="inherit" /> : <TokenIcon />}>
                        {firmaDialog.firmando ? 'Firmando...' : 'Firmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Progress dialog */}
            <Dialog open={progresoDialog.open} maxWidth="sm" fullWidth>
                <DialogTitle>{progresoDialog.tipo === 'generar' ? 'Generando PDFs' : progresoDialog.tipo === 'firmar' ? 'Firmando digitalmente' : 'Procesando'}</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <Typography variant="body2">Procesando {progresoDialog.actual} de {progresoDialog.total}</Typography>
                        <Typography variant="caption" color="text.secondary">{progresoDialog.currentName}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progresoDialog.total > 0 ? (progresoDialog.actual / progresoDialog.total) * 100 : 0} style={{ height: 8, borderRadius: 4 }} />
                    <Box mt={1} display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">{progresoDialog.generados} procesado(s)</Typography>
                        {progresoDialog.errores > 0 && <Typography variant="caption" color="error">{progresoDialog.errores} error(es)</Typography>}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Upload signed PDF dialog */}
            <Dialog open={uploadDialog.open} onClose={() => setUploadDialog({ open: false, docId: null })} maxWidth="sm" fullWidth>
                <DialogTitle><Box display="flex" alignItems="center" gap={1}><UploadIcon color="primary" />Subir PDF Firmado</Box></DialogTitle>
                <DialogContent>
                    <Typography paragraph>Suba el PDF firmado digitalmente.</Typography>
                    <input type="file" accept=".pdf" onChange={handleSubirFirmado} ref={fileInputRef} style={{ display: 'none' }} id="upload-pdf-panel" />
                    <label htmlFor="upload-pdf-panel">
                        <Button variant="contained" component="span" startIcon={<UploadIcon />} fullWidth>Seleccionar archivo PDF</Button>
                    </label>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialog({ open: false, docId: null })}>Cancelar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CartasPoder;
